/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { Order, OrderFoodTypeStatus } from "@lemmonhq/types";
import { getOrderFoodTypeStatusColor } from "@app/utils/index";
import { getStatusButtonText, canChangeStatus } from "./helpers";
import { useLocalization } from "@lemmonhq/uikit";

interface OrderCardFoodTypeStatusButtonProps {
  foodTypeStatus: OrderFoodTypeStatus;
  onStatusChange: (foodTypeId: number, currentStatus: string) => Promise<void>;
  loading?: boolean;
  order: Order;
}

export const OrderCardFoodTypeStatusButton =
  defineComponent<OrderCardFoodTypeStatusButtonProps>({
    name: "OrderCardFoodTypeStatusButton",
    props: ["foodTypeStatus", "onStatusChange", "loading", "order"],
    setup(props) {
      const { t } = useLocalization();
      const isLoading = ref(false);

      const handleStatusChange = async () => {
        if (!canChangeStatus(props.foodTypeStatus.status) || isLoading.value) {
          return;
        }

        isLoading.value = true;
        try {
          await props.onStatusChange(
            props.foodTypeStatus.id,
            props.foodTypeStatus.status,
          );
        } finally {
          isLoading.value = false;
        }
      };

      const loadingNode = (
        <span class="flex items-center justify-center">
          <span class="loading loading-xs loading-spinner loading mr-2"></span>
        </span>
      );

      return () => {
        const { foodTypeStatus } = props;
        const loading = props.loading || isLoading.value;
        const disabled = !canChangeStatus(foodTypeStatus.status) || loading;
        if (props.order.service_method === "takeaway") {
          return null;
        }

        return (
          <button
            style={getOrderFoodTypeStatusColor(foodTypeStatus.status)}
            onClick={handleStatusChange}
            disabled={disabled}
            data-status={foodTypeStatus.status}
            class={`${
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } text-center font-semibold px-3 py-1 rounded w-full`}
          >
            {loading
              ? loadingNode
              : getStatusButtonText(foodTypeStatus.status, t)}
          </button>
        );
      };
    },
  });
